<template>
  <v-container fluid>
    <v-card elevation="2">
      <v-card-title class="d-flex align-center pe-2 tableheader white--text">
        <v-icon left color="white" class="mr-2">mdi-office-building</v-icon>
        Rental Units
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          density="compact"
          label="Search Units (Name, Address, Desc.)"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
          class="me-2 search-field"
        ></v-text-field>
        <v-btn 
          color="primary" 
          @click="openAddDialog" 
          :loading="dialogLoading"
          v-if="authStore.canEdit"
        >
          <v-icon left>mdi-plus</v-icon> Add Unit
        </v-btn>
        
        <!-- <ExportButtons entity="units" filename="Units" :exclude="['id','created_at','updated_at', 'daily_rate', 'weekly_rate', 'image_1', 'image_2']"></ExportButtons> -->
        <ExportButtons entity="units" filename="Units" :headers="headers"></ExportButtons>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        :headers="headers"
        :items="units"
        :search="search"
        item-value="id"
        class="elevation-1"
        :loading="tableLoading"
        loading-text="Loading units... Please wait"
        hover
        show-expand
        v-model:expanded="expanded"
      >
        <template v-slot:expanded-row="{ columns, item }">
          <tr>
            <td :colspan="columns.length">
              <v-card flat class="pa-3 my-2" color="grey-lighten-5">
                <v-card-subtitle class="pb-1 font-weight-bold">Description:</v-card-subtitle>
                <v-card-text class="text-body-2" style="white-space: pre-wrap;">
                    {{ item.description || 'No description provided.' }}
                </v-card-text>
                </v-card>
            </td>
          </tr>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip location="top" text="View Details">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="openViewDialog(item)" color="info">
                mdi-eye-outline
              </v-icon>
            </template>
          </v-tooltip>
          <v-tooltip location="top" text="Edit Unit" v-if="authStore.canEdit">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small class="me-1" @click="editItem(item)" color="secondary">
                mdi-pencil
              </v-icon>
            </template>
          </v-tooltip>
          <v-tooltip location="top" text="Delete Unit" v-if="authStore.isAdmin">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" small @click="deleteItemPrompt(item)" color="error">
                mdi-delete
              </v-icon>
            </template>
          </v-tooltip>
        </template>

        <template v-slot:[`item.monthly_rate`]="{ value }">
          {{ formatCurrency(value) }}
        </template>
        <template v-slot:[`item.weekly_rate`]="{ value }">
          {{ formatCurrency(value) }}
        </template>
        <template v-slot:[`item.daily_rate`]="{ value }">
          {{ formatCurrency(value) }}
        </template>
        <!-- <template v-slot:[`item.data-table-expand`]="{ item, toggleExpand, isExpanded }"> <v-btn icon variant="text" @click="() => toggleExpand(item)" size="small">
                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
        </template> -->

        <template v-slot:no-data>
          <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
            {{ tableLoading ? 'Fetching data...' : 'No units available or match your search.' }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" persistent max-width="750px" @update:modelValue="val => !val && closeDialog()">
      <v-card :loading="dialogLoading">
        <v-card-title class="tableheader white--text">
          <span class="text-h5">{{ formTitle }}</span>
        </v-card-title>
        <v-form ref="unitForm" v-model="isFormValid" @submit.prevent="saveItem">
          <v-card-text class="mt-4">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field v-model="editedItem.name" label="Name*" variant="outlined" :rules="[rules.required, rules.maxLength(255)]" counter="255" required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field v-model="editedItem.address" label="Address*" variant="outlined" :rules="[rules.required]" required></v-text-field>
                </v-col>
                </v-row>
                
                <!-- <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                  :items="companies"
                  item-title="name"
                  item-value="id"
                  v-model="editedItem.company_id"
                  label="Company*"
                  variant="outlined"
                  class="mb-2"
                  :rules="[rules.required]"
                />  
                  </v-col>
                </v-row> -->

                <v-row>
                <v-col cols="12">
                  <v-textarea v-model="editedItem.description" label="Description" variant="outlined" rows="3" auto-grow counter></v-textarea>
                </v-col>
		
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editedItem.bedrooms"  type="number" label="Bedrooms" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editedItem.bathrooms" type="number" label="Bathrooms" variant="outlined" />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.number="editedItem.size_sqft" type="number" label="Size (sq ft)" variant="outlined"  />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="editedItem.unit_type"
                :items="unitTypes"
                item-title="label"
                item-value="value"
                label="Unit type" variant="outlined"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                v-model="editedItem.status"
                :items="statusOptions"
                item-title="label"
                item-value="value"
                label="Status" variant="outlined"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea v-model="editedItem.amenities" label="Amenities (comma-separated)" rows="3" variant="outlined"/>
            </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" sm="4">
                  <v-text-field v-model.number="editedItem.monthly_rate" label="Monthly Rate*" type="number" :prefix="getCurrencyPrefix()" variant="outlined" :rules="[rules.required, rules.positiveNumber]" required></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model.number="editedItem.weekly_rate" label="Weekly Rate*" type="number" :prefix="getCurrencyPrefix()" variant="outlined" :rules="[rules.required, rules.positiveNumber]" required></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model.number="editedItem.daily_rate" label="Daily Rate*" type="number" :prefix="getCurrencyPrefix()" variant="outlined" :rules="[rules.required, rules.positiveNumber]" required></v-text-field>
                </v-col>
            </v-row>
            
            <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.water_meter_nbr" label="Water Meter Number" variant="outlined" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editedItem.electricity_meter_nbr" label="Electricity Meter Number" variant="outlined" />
                  </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6">
                  <v-file-input v-model="imageFile1" label="Image 1 (Optional)" accept="image/png, image/jpeg, image/webp" variant="outlined" prepend-icon="mdi-camera" show-size @change="handleImageUpload('image_1_base64', $event, 'imagePreview1')" @click:clear="clearImage('image_1_base64', 'imagePreview1', 'imageFile1')"></v-file-input>
                  <v-img v-if="imagePreview1" :src="imagePreview1" max-height="150" contain class="mt-2 mb-2 elevation-1 rounded"></v-img>
                  <v-btn v-if="editedItem.image_1_base64 && !imageFile1" small color="warning" @click="clearExistingImage('image_1_base64', 'imagePreview1')" class="mt-1 text-caption">Clear Existing Image 1</v-btn>
                </v-col>
                <v-col cols="12" md="6">
                  <v-file-input v-model="imageFile2" label="Image 2 (Optional)" accept="image/png, image/jpeg, image/webp" variant="outlined" prepend-icon="mdi-camera" show-size @change="handleImageUpload('image_2_base64', $event, 'imagePreview2')" @click:clear="clearImage('image_2_base64', 'imagePreview2', 'imageFile2')"></v-file-input>
                  <v-img v-if="imagePreview2" :src="imagePreview2" max-height="150" contain class="mt-2 mb-2 elevation-1 rounded"></v-img>
                  <v-btn v-if="editedItem.image_2_base64 && !imageFile2" small color="warning" @click="clearExistingImage('image_2_base64', 'imagePreview2')" class="mt-1 text-caption">Clear Existing Image 2</v-btn>
                </v-col>
              </v-row>
              <!-- <v-row>
                <v-col cols="12" md="6">
                  
                </v-col>
              </v-row> -->
            </v-container>
            <small class="pl-3">*indicates required field</small>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey-darken-1" variant="text" @click="closeDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" type="submit" :loading="dialogLoading" :disabled="!isFormValid">Save Unit</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <v-dialog v-model="viewDialog" max-width="800px" @update:modelValue="val => !val && closeViewDialog()">
      <v-card :loading="viewDialogLoading">
        <v-card-title class="tableheader d-flex align-center" style="background-color: var(--v-primary-base)">
          <v-icon left color="white" class="mr-2">mdi-home-city-outline</v-icon>
          <span class="text-h5 white--text">Unit Details: {{ viewItem?.name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" color="white" @click="closeViewDialog"></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text v-if="viewItem" class="pt-4">
          <v-list lines="two" density="compact">
            <!-- <v-list-item title="ID" :subtitle="viewItem.id"></v-list-item><v-divider inset></v-divider> -->
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Name:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.name }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Address:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.address }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Water Meter Number:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.water_meter_nbr }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Electricity Meter Number:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.electricity_meter_nbr }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <!-- <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Company:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ findName(companies, viewItem.company_id) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider> -->
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Description:</div>
              </template>
              <v-list-item-text class="text-left pl-2"> <div style="white-space: pre-wrap; text-align: left;">{{ viewItem.description || 'N/A' }}</div></v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Bedrooms:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.bedrooms }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Bathrooms:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.bathrooms }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Size (sq ft):</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.size_sqft }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Unit Type:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.unit_type }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Status:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ viewItem.status }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Monthly Rate:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ formatCurrency(viewItem.monthly_rate) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Weekly Rate:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ formatCurrency(viewItem.weekly_rate) }}</v-list-item-text>
            </v-list-item><v-divider inset></v-divider>
            <v-list-item>
              <template v-slot:prepend>
                <div class="font-weight-medium">Daily Rate:</div>
              </template>
              <v-list-item-text class="text-right pl-2"> {{ formatCurrency(viewItem.daily_rate) }}</v-list-item-text>
            </v-list-item>
          </v-list>
          <v-row class="mt-4">
            <v-col cols="12" md="6" v-if="viewItem.image_1_base64">
              <v-card-subtitle class="mb-1">Image 1</v-card-subtitle>
              <v-img :src="`data:image/jpeg;base64,${viewItem.image_1_base64}`" aspect-ratio="1.77" class="elevation-2 rounded" cover alt="Unit Image 1"></v-img>
            </v-col>
            <v-col cols="12" md="6" v-else-if="viewDialogLoading === false">
                <v-card-subtitle class="mb-1">Image 1</v-card-subtitle>
                <v-alert type="info" variant="tonal" density="compact" class="text-center fill-height d-flex align-center justify-center">No image provided.</v-alert>
            </v-col>
            <v-col cols="12" md="6" v-if="viewItem.image_2_base64">
              <v-card-subtitle class="mb-1">Image 2</v-card-subtitle>
              <v-img :src="`data:image/jpeg;base64,${viewItem.image_2_base64}`" aspect-ratio="1.77" class="elevation-2 rounded" cover alt="Unit Image 2"></v-img>
            </v-col>
             <v-col cols="12" md="6" v-else-if="viewDialogLoading === false">
                <v-card-subtitle class="mb-1">Image 2</v-card-subtitle>
                <v-alert type="info" variant="tonal" density="compact" class="text-center fill-height d-flex align-center justify-center">No image provided.</v-alert>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text v-else class="text-center py-5">
            <v-progress-circular indeterminate color="primary" class="mb-3"></v-progress-circular>
            <p>Loading unit details...</p>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="tonal" @click="closeViewDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card :loading="dialogLoading">
        <v-card-title class="text-h5 error--text">Confirm Deletion</v-card-title>
        <v-card-text>Are you sure you want to delete unit: <strong>{{ itemToDelete?.name }}</strong>?</v-card-text>
        <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" text @click="closeDeleteDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="error" @click="confirmDelete" :loading="dialogLoading">Delete</v-btn>
            <v-spacer></v-spacer>
        </v-card-actions>
        </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import UnitService from '@/services/UnitService';
import { useAuthStore } from '@/stores/auth'; // Import the auth store
import { formatCurrency, getCurrencyPrefix, useSnackbar } from '@/services/genFunctions';
import ExportButtons from '@/components/ExportButtons.vue';
// import CompanyService from '@/services/CompaniesService';

const authStore = useAuthStore(); // Initialize the auth store
const { snackbar, showSnackbar } = useSnackbar();

const search = ref('');
const tableLoading = ref(true);
const dialog = ref(false);
const dialogLoading = ref(false);
const deleteDialog = ref(false);
const unitForm = ref(null);
const isFormValid = ref(false);
const expanded = ref([]);

const viewDialog = ref(false);
const viewDialogLoading = ref(false);
const viewItem = ref(null);
// const companies = ref([]);

const defaultItem = {
  id: null, name: '', address: '', company_id: null, water_meter_nbr: null, electricity_meter_nbr: null,
  monthly_rate: null, weekly_rate: null, daily_rate: null,
  description: '',
  bedrooms: null, bathrooms: null, size_sqft: null,
  amenities: '',
  unit_type: 'apartment',
  status: 'available',
  image_1_base64: null, image_2_base64: null,
};

const unitTypes = [
  { label: 'Apartment',  value: 'apartment' },
  { label: 'House',      value: 'house' },
  { label: 'Studio',     value: 'studio' },
  { label: 'Commercial', value: 'commercial' },
];

const statusOptions = [
  { label: 'Available',   value: 'available' },
  { label: 'Occupied',    value: 'occupied' },
  { label: 'Maintenance', value: 'maintenance' },
];

const editedItem = ref({ ...defaultItem });
const itemToDelete = ref(null);

const imageFile1 = ref(null);
const imageFile2 = ref(null);
const imagePreview1 = ref(null);
const imagePreview2 = ref(null);

const rules = {
  required: value => !!value || 'This field is required.',
  positiveNumber: value => (value === null || value === undefined || value === '') || (typeof value === 'number' && value > 0) || 'Must be a positive number > 0.',
  maxLength: (len) => value => (value || '').length <= len || `Maximum ${len} characters.`,
};

const headers = ref([
  { title: 'Name', key: 'name', align: 'start', sortable: true, width: '20%' },
  { title: 'Address', key: 'address', align: 'start', sortable: true, width: '30%' },
  { title: 'Unit Type', key: 'unit_type', align: 'start', sortable: true, width: '10%' },
  { title: 'Monthly Rate', key: 'monthly_rate', align: 'end', sortable: true },
  // { title: 'Weekly', key: 'weekly_rate', align: 'end', sortable: true },
  // { title: 'Daily', key: 'daily_rate', align: 'end', sortable: true },
  { title: 'Status', key: 'status', align: 'start', sortable: true, width: '10%' },
  { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '130px' }
]);

const units = ref([]);

const fetchUnits = async () => {
  tableLoading.value = true;
  try {
    units.value = await UnitService.getUnits();
  } catch (error) {
    // Error handling (e.g., 401/403) should ideally be caught by Axios interceptor
    // If not, or for specific handling:
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        showSnackbar(error.response.data.error || 'You are not authorized to view units.', 'error');
    } else {
        showSnackbar('Failed to load units.', 'error');
    }
    console.error("Error fetching units:", error);
    units.value = [];
  } finally {
    tableLoading.value = false;
  }
};

// const fetchCompanies = async () => {
//   try {
//     companies.value = await CompanyService.getCompanies();
//   } catch (error) {
//     showSnackbar('Failed to load companies.', 'error');
//     console.error("Error fetching companies:", error);
//     companies.value = [];
//   }
// };

// function findName(list, id) {
//   if (!list || !Array.isArray(list) || !id) return '�';
//   const obj = list.find(i => i && i.id === id);
//   return obj ? obj.name : `Unknown (ID: ${id})`;
// }

onMounted(async () => {
  await fetchUnits();
  // await fetchCompanies();
});

const formTitle = computed(() => !editedItem.value.id ? 'New Unit' : 'Edit Unit');

const handleImageUpload = (itemKey, event, previewKey) => {
  const file = event.target.files[0];
  if (!file) {
    editedItem.value[itemKey] = null;
    if (previewKey === 'imagePreview1') imagePreview1.value = null;
    if (previewKey === 'imagePreview2') imagePreview2.value = null;
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    editedItem.value[itemKey] = e.target.result;
    if (previewKey === 'imagePreview1') imagePreview1.value = e.target.result;
    if (previewKey === 'imagePreview2') imagePreview2.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const clearImage = (itemKey, previewKey, fileModelKey) => {
    editedItem.value[itemKey] = null;
    if (previewKey === 'imagePreview1') imagePreview1.value = null;
    if (previewKey === 'imagePreview2') imagePreview2.value = null;
    if (fileModelKey === 'imageFile1') imageFile1.value = null;
    if (fileModelKey === 'imageFile2') imageFile2.value = null;
};

const clearExistingImage = (itemKey, previewKey) => {
    editedItem.value[itemKey] = ""; 
    if (previewKey === 'imagePreview1') imagePreview1.value = null;
    if (previewKey === 'imagePreview2') imagePreview2.value = null;
};

const openAddDialog = () => {
  editedItem.value = { ...defaultItem };
  imageFile1.value = null; imageFile2.value = null;
  imagePreview1.value = null; imagePreview2.value = null;
  dialog.value = true;
  nextTick(() => unitForm.value?.resetValidation());
};

const editItem = async (item) => {
  dialogLoading.value = true;
  try {
    const fullUnitData = await UnitService.getUnit(item.id);
    editedItem.value = { ...fullUnitData };
    imagePreview1.value = fullUnitData.image_1_base64 ? `data:image/jpeg;base64,${fullUnitData.image_1_base64}` : null;
    imagePreview2.value = fullUnitData.image_2_base64 ? `data:image/jpeg;base64,${fullUnitData.image_2_base64}` : null;
    imageFile1.value = null; imageFile2.value = null;
    dialog.value = true;
    nextTick(() => unitForm.value?.resetValidation());
  } catch (error) {
    showSnackbar('Failed to load unit details for editing.', 'error');
    console.error("Error fetching unit for edit:", error);
  } finally {
    dialogLoading.value = false;
  }
};

const deleteItemPrompt = (item) => {
  itemToDelete.value = { ...item };
  deleteDialog.value = true;
};

const closeDialog = async () => {
  dialog.value = false;
  await nextTick();
  editedItem.value = { ...defaultItem };
  imageFile1.value = null; imageFile2.value = null;
  imagePreview1.value = null; imagePreview2.value = null;
  unitForm.value?.resetValidation();
  dialogLoading.value = false;
};

const closeDeleteDialog = () => {
    deleteDialog.value = false;
    nextTick(() => itemToDelete.value = null);
    dialogLoading.value = false;
};

const saveItem = async () => {
  if (!unitForm.value) return;
  const { valid } = await unitForm.value.validate();
  if (!valid) {
    showSnackbar('Please correct the form errors.', 'warning');
    return;
  }
  dialogLoading.value = true;
  const payload = { ...editedItem.value };
  try {
    if (payload.id) {
      const updatedUnit = await UnitService.updateUnit(payload.id, payload);
      const index = units.value.findIndex(u => u.id === updatedUnit.id);
      const dataToUpdate = { ...updatedUnit };
      if (payload.image_1_base64 === "" && !updatedUnit.image_1_base64) dataToUpdate.image_1_base64 = null;
      if (payload.image_2_base64 === "" && !updatedUnit.image_2_base64) dataToUpdate.image_2_base64 = null;
      if (index > -1) units.value.splice(index, 1, dataToUpdate);
      else await fetchUnits();
      showSnackbar('Unit updated successfully!', 'success');
    } else {
      const newUnit = await UnitService.createUnit(payload);
      units.value.unshift(newUnit);
      showSnackbar('Unit created successfully!', 'success');
    }
    closeDialog();
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Could not save unit.';
    showSnackbar(errorMsg, 'error', 6000);
    console.error("Error saving unit:", error.response || error);
  } finally {
    dialogLoading.value = false;
  }
};

const confirmDelete = async () => {
    if (!itemToDelete.value?.id) return;
    dialogLoading.value = true;
    try {
        await UnitService.deleteUnit(itemToDelete.value.id);
        units.value = units.value.filter(u => u.id !== itemToDelete.value.id);
        showSnackbar('Unit deleted successfully!', 'success');
        closeDeleteDialog();
    } catch (error) {
        const errorMsg = error.response?.data?.error || 'Could not delete unit.';
        showSnackbar(errorMsg, 'error', 6000);
        console.error("Error deleting unit:", error.response || error);
    } finally {
        dialogLoading.value = false;
    }
};

const openViewDialog = async (item) => {
  viewDialogLoading.value = true;
  viewDialog.value = true;
  viewItem.value = null; 
  try {
    const fullUnitData = await UnitService.getUnit(item.id);
    viewItem.value = fullUnitData;
  } catch (error) {
    const errorMsg = error.response?.data?.error || 'Failed to load unit details.';
    showSnackbar(errorMsg, 'error');
    console.error("Error fetching unit details for view:", error);
    viewDialog.value = false; 
  } finally {
    viewDialogLoading.value = false;
  }
};

const closeViewDialog = () => {
  viewDialog.value = false;
};

</script>

<style scoped>
/* .v-card-title.tableheader {
  background-color: var(--v-theme-tableheader) !important;
  color: white !important;
}
.tableheader .v-icon { color: white !important; } */
.search-field .v-input__control .v-field { background-color: rgba(255, 255, 255, 0.9) !important; }
.search-field label { color: rgba(0, 0, 0, 0.6) !important; }
.error--text { color: rgb(var(--v-theme-error)); }
.v-img.rounded { border-radius: 4px; } 
.v-list-item--density-compact .v-list-item-title { font-weight: 500; }
.v-list-item--density-compact .v-list-item-subtitle { white-space: normal; } 
.fill-height { min-height: 100%; } /* For alert in image column */
</style>